import React, {useEffect, useRef, useState} from 'react'
import {SlotContentItem} from '../../../types/cms'
import {usePromotionDataTracking} from '../../../analytics/promotion-tracking'
import {Stack, Box} from '../../../vanilla'
import {Slot, useContentToDisplay} from '../../../components/cms/Slot'
import {SlotContentType} from '../../category'
import {DefaultContentBody} from 'dc-delivery-sdk-js'
import {DateUtils} from '../../../utils/date-utils'
import { hitsGrid } from '../../../components/algolia/instantsearch/styles.css'
import classNames from 'classnames'
import { BuildArrayForPLPProps, CreatePLPSectionProps, GetRowSlotProps } from '../../../types/algolia'
import { ProductTile } from '../../../components/product-tile'
import { isHitPromoGridCard, getVirtualGridCount } from '../../../components/algolia/util/utils'
import { ANALYTICS_CAROUSEL_NAME } from '../../../constants'
import { Hit } from '../../../store/ProductStore'
import { useIntersectionObserver } from '../../../hooks/use-intersection-observer'
import { SLOT_SCHEMAS } from '../../../components/cms/utils'
import { DisplayRetailMediaSlot } from './display-retail-media-slot'
import { RetailMediaBanner, BannerSlotResponse } from '../../../types/retail-media'
import { BannerRowSkeleton } from '../../../components/skeletons'

export interface DisplaySlotsProps {
  slots: SlotContentItem
  slotIndex?: number
  slotGroupName?: string
}

const getSlotElement = (slots: SlotContentItem, slotIndex: number) => {
  return <Slot data={slots} slotIndex={slotIndex} />
}

export const DisplaySlots = ({slots, slotIndex, slotGroupName}: DisplaySlotsProps) => {
  const sendPromotionData = usePromotionDataTracking()
  const ref = useRef<HTMLDivElement | null>(null)

  const [slotElement, setSlotElement] = useState<JSX.Element>(getSkeleton())
  const [slotLoaded, setSlotLoaded] = useState<boolean>(false)

  // Ensure correct info from content being send to datalayer (i.e. default/variations)
  const slotsToDisplay = useContentToDisplay(slots)
  const _slotGroupName = slotGroupName ?? slots._meta?.name 

  // GTM Tracking
  const { isOnScreen: isTrueView } = useIntersectionObserver(ref, {
    useOnce: true,
    /* Offset for intersection track. isOnScreen will only become true as the customer scrolls and the slot comes into view.
    -70px means isOnScreen will only become true after the customer can see at least 70px of the content, preventing the 
    tracking from firing before the customer has seen the content */
    rootMargin: '-70px 0px -70px 0px',
  })

  // Lazy Loading
  const { isOnScreen: shouldPreload } = useIntersectionObserver(ref, {
    useOnce: false,
    // Add a 200px offset as a buffer to preload just before the slot enters the screen
    // Helps avoid visible loading & layout shifting
    rootMargin: '0px 0px 200px 0px'
  })

  useEffect(() => {
    if (shouldPreload && slots && slotIndex && !slotLoaded) { 
      setSlotLoaded(true)
      setSlotElement(getSlotElement(slots, slotIndex))
    }
  }, [shouldPreload, slotsToDisplay])

  useEffect(() => {
    if (isTrueView && slotsToDisplay) { 
      const promoContent = slotsToDisplay?.map((slot) => ({
      ...slot,
      _slotGroupName,
      slotIndex
    }))
      sendPromotionData([promoContent])
    }
  }, [isTrueView, slotsToDisplay])

  return (
    <>
    {slotsToDisplay && 
      <Stack gap={['10px', '0px']} marginBottom={['10px', '20px']} ref={ref}>
        {slotElement}
      </Stack> 
      }
    </>
  )
}

export const getBottomSlot = (slots?: SlotContentItem) => {
  const bottomSlot =
    slots &&
    slots?.length > 0 &&
    slots?.map(
      (slotGroup: SlotContentType, idx: number) =>
        slotGroup?._meta?.schema === SLOT_SCHEMAS.CATEGORY_BOTTOM_SLOT && (
          <DisplaySlots slots={slotGroup} slotIndex={idx + 1} key={`${slotGroup?._meta?.deliveryId}-${idx}`} />
        ),
    )
  return bottomSlot
}

export const getSkeleton = () => {
  return <BannerRowSkeleton />
}

// Check for In-Grid Promo Content and Filter out any expired Content
export const getInGridPromos = (page?: DefaultContentBody) => {
  let inGridPromos: (SlotContentType & {originalSlotIndex?: number}) | undefined

  if (page?.slots && page.slots.length > 0) {
    for (let idx = 0; idx < page.slots.length; idx++) {
      const slotGroup = page.slots[idx]
      
      if (slotGroup?._meta?.schema !== SLOT_SCHEMAS.IN_GRID_POSITIONING_SLOT) {
        continue
      }

      // Check if we have a lifecycle expiry time
      if (!slotGroup?._meta?.lifecycle?.expiryTime) {
        // No Expiry time set, this must be default content, let it render
        inGridPromos = {
          ...slotGroup,
          originalSlotIndex: idx + 1
        }
        break
      }

      // Check if we're between the start/end dates for the edition
      const expiryTime = new Date(slotGroup._meta.lifecycle.expiryTime)

      // Should Return True if we haven't hit the expiry time yet
      if (DateUtils.isBefore(new Date(), expiryTime)) {
        inGridPromos = {
          ...slotGroup,
          originalSlotIndex: idx + 1
        }
        break
      }
    }
  }

  return inGridPromos
}

export const getSeo = (page?: DefaultContentBody) => {
  // Find the seo content slot (category pages)
  const seoSlot = page?.slots?.find(
    (slot: {_meta?: {schema?: string}; seo?: {pageTitle?: string; pageDescription?: string}}) =>
      slot?._meta?.schema === SLOT_SCHEMAS.SEO_PAGE_CONTENT,
  )

  // Create the seo object
  const seo = {
    pageTitle: seoSlot?.seo?.pageTitle || page?.seo?.pageTitle || `${page?.name} | Iceland Foods`,
    pageDescription: seoSlot?.seo?.pageDescription || page?.seo?.pageDescription,
  }
  return seo
}

/**
 * Looks through all the possible Category / Search Row Slots on the page and 
 * returns the first two slots in the array to be rendered in their respective positions
 * @param { DefaultContentBody[] } rowSlots - an array of Category Row Slots on the page
 * @returns an object with two slots, one for each position on the page
 */
export const getRowSlotPosition = (rowSlots?: DefaultContentBody[]) => {
  if (!rowSlots) return
  
  // Take the first slot as positionOne and second as positionTwo
  const positionOne = rowSlots[0]
  const positionTwo = rowSlots[1]

  return { positionOne, positionTwo }
}

/**
 * Renders the correct Category Row Slot in the right position on the page
 * @param props:
 * - i: index within loop,
 * - flatList: array of hits and inGridCards
 * - rowSlotPositions: object with Slots and their positions
 * - screenSize: current device
 * - sectionStartIndex: the item starting index for the current section
 * - sectionEndIndex: the item end index for the current section
 * @returns the correct banner for that slot, if it should be rendered
 */
function getRowSlot({
  i,
  flatList,
  rowSlotPositions,
  screenSize,
  sectionStartIndex,
  sectionEndIndex
}: GetRowSlotProps) {
  let showBanner = true
  let slotData: DefaultContentBody | RetailMediaBanner | undefined

  if (i === 0) {
    slotData = rowSlotPositions?.positionOne
    showBanner = flatList.length >= 1
  }

  if (i === 1) {
    slotData = rowSlotPositions?.positionTwo
    const itemsPerRow = {
      'desktop': 5,
      'tablet': 3,
      'mobile': 1
    } 
    if (screenSize && flatList.length > 0) {
      if (screenSize === 'mobile') {
        // Mobile only has 1 product per row, ensure that we have at least 5 rows to output the 2nd banner
        showBanner = flatList.length >= 5
      }
      else {
        const sectionItems = flatList.slice(sectionStartIndex, sectionEndIndex)
        const virtualCount = getVirtualGridCount(sectionItems)
        // Ensure that we have more than 1 row in the current section before rendering the banner
        showBanner = virtualCount > itemsPerRow[screenSize]
      }
    }
    else {
      showBanner = false
    }
  }

  if (showBanner && slotData) {
    // Conditionally show the right component
    if (slotData.retailMediaContent) {
      return (

        <Box key={`banner-${i}`} width="full" paddingBottom={['10px', '0px']}>
          <DisplayRetailMediaSlot slot={slotData as RetailMediaBanner} slotIndex={slotData.slotPosition} slotGroupName={slotData.slotGroupName} />
        </Box>
      )
    }
    return (
      <Box key={`banner-${i}`} width="full" paddingBottom={['0px']}> 
        <DisplaySlots slots={slotData as DefaultContentBody} slotIndex={slotData?.originalSlotIndex} key={`row-slots-${i}`} />
      </Box>
    )
  }
  return null
}

/**
 * Builds the array of ReactNodes of hits and inGridCards for the PLP
 * @param props: 
 * - hitsAndPromos: an array containing hits and slots
 * - itemListName, itemListId, categoryId, pageStartQuery - info for tagging purposes
 * - options
 * @returns flatList: an array of ReactNodes
 */
export const buildArrayForPLP = ({
  hitsAndPromos,
  itemListName,
  itemListId,
  categoryId,
  searchQuery,
  pageStartQuery,
  options
}: BuildArrayForPLPProps) => {

  const flatList: React.ReactNode[] = []
  let hitIndex = 0
  
  for (let i = 0; i < hitsAndPromos.length; i++) {
    const hit = hitsAndPromos[i]
    if (isHitPromoGridCard(hit)) {
          flatList.push(<Slot data={hit} key={`slot-${i}`} gridItemType="promo" />) 
        } else {
          flatList.push(
            // Unknown for now - can be reverted when ingrid cards removed
              <ProductTile
                hit={hitsAndPromos[i] as unknown as Hit}
                idx={i}
                isPLPTile
                itemListName ={itemListName || ANALYTICS_CAROUSEL_NAME}
                itemListId={itemListId || categoryId || ''}
                key={`${hitsAndPromos[i].id}-${i}`}
                pageStartQuery={pageStartQuery}
                hitItemIndex={hitIndex}
                viewingCategoryId={categoryId}
                searchQuery={searchQuery}
                gridItemType="product"
                isSponsored={hit?.isSponsored === true}
                sponsoredAdId={hit?.adId as string}
                {...options}
              />
          )
          hitIndex++
        }
    }
  return flatList
}

/**
 * Creates the sections for the PLP page: blocks of hits split by Category
 * Row Banners. The size of each chunk of hits varies per device
 * @param props: 
 * - flatList: the array of hits and inGridCards, 
 * - rowEndIndexes: The point at which each section of hits ends
 * - rowSlotPositions: Which slot each banner should sit in (one or two)
 * - shouldShowBanners: boolean check that we're on the first page of results
 * - screenSize: string for the current device size (mobile, tablet, desktop)
 * - className: string to add styles if passed in
 * @returns array of ReactNodes to be rendered on the page
 */
export const createPLPSections = (props: CreatePLPSectionProps) => {
  const {
    flatList,
    rowEndIndexes = [],
    rowSlotPositions,
    screenSize,
    className
  } = props
  const sections = []
    let start = 0
    for (let i = 0; i < rowEndIndexes.length; i++) {
      const end = rowEndIndexes[i]
      sections.push(
        <Box 
          key={`hits-row-${i}`}
          className={classNames(hitsGrid, className)}
          paddingBottom={['10px', '20px']}
          >
          {flatList.slice(start, end)}
        </Box>
      )
        const getRowSlotInfo = {i, flatList, rowSlotPositions, screenSize, sectionStartIndex: start, sectionEndIndex: end}
        const rowSlot = getRowSlot(getRowSlotInfo)
        if (rowSlot) sections.push(rowSlot)
      start = end
    }
    if (start < flatList.length) {
      sections.push(
        <Box key="hits-row-final" className={classNames(hitsGrid, className)}>
          {flatList.slice(start)}
        </Box>
      )
    }
  return sections
}

/**
 * Gets the PLP Top Slot from the content in Amplience
 * @param { DefaultContentBody } data page data from Amplience
 * @returns {BannerSlotResponse} Array of top slots
 */

export const getBannerSlot = (data?: DefaultContentBody): BannerSlotResponse => {
  const response = { topSlot: [] }
  if (!data) return response
  response.topSlot =
    data?.slots &&
    data?.slots?.length > 0 &&
    data?.slots?.map((slotGroup: SlotContentType, idx: number) => {
      const isCategoryPageBanner = slotGroup?._meta?.schema === SLOT_SCHEMAS.CATEGORY_BANNER_SLOT
      const isSearchPageBanner = slotGroup?._meta?.schema === SLOT_SCHEMAS.SEARCH_RESULTS_TOP_SLOT
      if (isCategoryPageBanner || isSearchPageBanner) {
        return <DisplaySlots slots={slotGroup} slotIndex={idx + 1} key={`${slotGroup?._meta?.deliveryId}-${idx}`} slotGroupName={slotGroup?._meta?.name} />
      }
    })
  return response
}

/**
 * Filter the page content data from Amplience and return all valid Category Row Slots or Search Results Slots with their original index
 * @param  { DefaultContentBody } data - page data from Amplience
 * @returns an array of PLP Row Slots with their original slot positions
 */
export const getRowSlots = (data?: DefaultContentBody) => {
  const rowSlots = data?.slots
    ?.map((slot: SlotContentType, idx: number) => ({
      ...slot,
      originalSlotIndex: idx + 1
    }))
    ?.filter((slot: SlotContentType) => slot._meta?.schema === SLOT_SCHEMAS.CATEGORY_ROW_SLOT || slot?._meta?.schema === SLOT_SCHEMAS.SEARCH_RESULTS_ROW_SLOT)

  return rowSlots
}